import React, { useState, useEffect } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import PostRegistrationForm from "./PostRegistrationForm";
import postRegistrationService from "../../services/postRegistrationService";
import { axiosInstance } from "../../services/axiosInstance";
import { useAuth } from "../../contexts/AuthContext";

interface PostRegistrationContainerProps {
  children: React.ReactNode;
}

const PostRegistrationContainer: React.FC<PostRegistrationContainerProps> = ({
  children,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const lang = i18n.language || "en";
  const [showForm, setShowForm] = useState(false);
  const [showMembershipSelection, setShowMembershipSelection] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkPostRegistrationStatus = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        // Check user subscription status via axiosInstance (uses the correct backend API URL)
        // Backend mounts subscription routes at /api/subscriptions (see backend/src/app.js).
        const axiosResponse = await axiosInstance.get(
          "/api/subscriptions/status",
        );
        const data = axiosResponse.data;

        if (data) {
          // Verificar el rol del usuario - teachers, especialistas y admins son Expert por defecto
          const role = data.data?.role;
          const rolesWithDefaultExpert = ["teacher", "especialista", "admin"];

          if (role && rolesWithDefaultExpert.includes(role)) {
            setIsLoading(false);
            return;
          }

          // Verificar múltiples campos para usuarios existentes
          const tier = data.data?.tier;
          const subscriptionStatus = data.data?.subscriptionStatus;
          const planType = data.data?.planType;
          const subscriptionTier = data.data?.subscriptionTier;
          const isActive = data.data?.isActive;
          const stripeSubscriptionId = data.data?.stripeSubscriptionId;
          const groupId = data.data?.groupId;

          // Extract subscription details

          // USUARIOS CON PLAN ACTIVO - NO mostrar selección
          // NOTE: We deliberately exclude `tier` from this check because `tier` is
          // a computed/legacy value — any user with role "user" returns tier "explorer"
          // even without a real subscription. We rely on actual subscription fields only.
          //
          // IMPORTANT: We check `stripeSubscriptionId` (an actual subscription), NOT
          // `stripeCustomerId`. Every new user gets a Stripe customer auto-created
          // during registration (see backend createUserProfile → stripeService.createCustomer),
          // so `stripeCustomerId` would be truthy for EVERY user and silently skip the
          // membership selector — the regression the user reported.
          const hasActivePlan =
            // Plan type activo (real subscription field, not computed)
            (planType && planType !== "free") ||
            (subscriptionTier && subscriptionTier !== "free") ||
            // Estado de suscripción activo
            (subscriptionStatus &&
              ["active", "trialing", "trial"].includes(subscriptionStatus)) ||
            // Flag isActive
            isActive === true ||
            // Tiene una suscripción real de Stripe (no solo un customer record)
            !!stripeSubscriptionId ||
            // Pertenece a un grupo
            !!groupId;

          if (hasActivePlan) {
            // User has active subscription, skip selection
            setIsLoading(false);
            return;
          }

          // No active plan signals at all → force the membership selector.
          // Rule: any non-Expert-by-default role (handled above) without an
          // active subscription MUST pick a plan before reaching the dashboard.
          // We deliberately do NOT require a long "isCompletelyNewUser" check:
          // partial/legacy state used to silently fall through and let users
          // skip onboarding. If hasActivePlan is false, show the selector.
          console.log(
            "[PostRegistrationContainer] No active plan detected — forcing membership selection",
            { planType, subscriptionTier, subscriptionStatus, isActive, role },
          );
          setShowMembershipSelection(true);
          setIsLoading(false);
          return;
        }

        // Si tiene suscripción, verificar post-registration normal
        const { completed } =
          await postRegistrationService.checkPostRegistrationStatus();

        if (!completed) {
          setShowForm(true);
        }
      } catch (error: any) {
        const status = error?.response?.status;
        console.error(
          "[PostRegistrationContainer] Error checking subscription status:",
          { status, message: error?.message, url: error?.config?.url },
        );

        // A 404 here means the frontend is calling a path the backend does not expose
        // (regression). Fail loudly so it is caught in dev/staging.
        if (status === 404) {
          console.error(
            "[PostRegistrationContainer] /api/subscriptions/status returned 404 — backend route mismatch. Check subscription.routes mount path.",
          );
        }

        // For NEW users (no real subscription, no plan info on the user object) we still
        // force the membership selector so a transient backend error cannot silently let
        // someone skip onboarding. Existing users with any plan signal are left alone.
        // NOTE: We check stripeSubscriptionId, NOT stripeCustomerId — every registered
        // user has a Stripe customer auto-created at registration.
        const u: any = user;
        const looksLikeNewUser =
          !!u &&
          !u.stripeSubscriptionId &&
          !u.planType &&
          !u.subscriptionTier &&
          !u.groupId &&
          (u.role === "cliente" || u.role === "user" || !u.role);

        if (looksLikeNewUser) {
          console.warn(
            "[PostRegistrationContainer] Subscription check failed for an apparently new user — forcing membership selection.",
          );
          setShowMembershipSelection(true);
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkPostRegistrationStatus();
  }, [user]);

  const handleComplete = () => {
    setShowForm(false);
    setShowMembershipSelection(false);
    navigate(`/${lang}/dashboard`);
  };

  const handleSkip = () => {
    setShowForm(false);
    setShowMembershipSelection(false);
    navigate(`/${lang}/dashboard`);
  };

  // Si no hay usuario autenticado, mostrar el contenido normal
  if (!user) {
    return <>{children}</>;
  }

  // Mientras se verifica la suscripción, renderizar el contenido normalmente.
  // El componente ya está montado (invisible) detrás del MovieIntroLoader,
  // así que la verificación ocurre mientras el loader sigue visible.
  // No mostramos un spinner propio para evitar el salto visual al apagarse el loader.
  if (isLoading) {
    return <>{children}</>;
  }

  if (
    showMembershipSelection &&
    location.pathname !== "/membership-selection"
  ) {
    return (
      <Navigate
        to="/membership-selection"
        replace
        state={{ fromRegistration: true }}
      />
    );
  }

  if (showForm) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <PostRegistrationForm
              onComplete={handleComplete}
              onSkip={handleSkip}
            />
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  return <>{children}</>;
};

export default PostRegistrationContainer;
